import type { OrderbookLevel } from "../sdk/types/data";

/**
 * Display-only extension for orderbook levels (e.g. fake/pending).
 * Used only in the enhancement layer; not sent to the server.
 */
export type EnhancedOrderbookLevel = OrderbookLevel & { isPending?: boolean };

export interface EnhancementConfig {
  /** Master switch: when false, no jitter or fake pending is applied. */
  enabled: boolean;
  jitter?: {
    /** Max ±% to vary price and size (e.g. 0.001 = ±0.1%). */
    amountPct: number;
    /** Interval in ms between jitter updates (e.g. 400–600). */
    intervalMs: number;
    /** Only sync from raw when total size (asks or bids) changes by at least this fraction (e.g. 0.03 = 3%). */
    amountChangeThresholdPct?: number;
    /** When raw is empty, seed levels around this mid so jitter still runs. */
    defaultMidPrice?: number;
    /** Number of default levels per side when raw is empty (default 5). */
    defaultLevelsCount?: number;
  };
  fakePending?: {
    /** Max number of fake levels per side (asks/bids). */
    maxFakeLevelsPerSide: number;
    minLifetimeMs: number;
    maxLifetimeMs: number;
    /** Fake size as % of nearby real level (e.g. 0.5–1.5). */
    volumeScalePct: number;
    /** Min delay before adding next fake (ms). */
    minAddDelayMs: number;
    /** Max delay before adding next fake (ms). */
    maxAddDelayMs: number;
  };
}

export const DEFAULT_JITTER: NonNullable<EnhancementConfig["jitter"]> = {
  amountPct: 0.411,
  intervalMs: 100,
  amountChangeThresholdPct: 1.304,
  defaultMidPrice: 70000,
  defaultLevelsCount: 20,
};

/**
 * Build seed levels when raw is empty so jitter has something to apply to.
 * Asks above mid (descending), bids below mid (descending from mid).
 */
export function getDefaultLevelsWhenEmpty(
  midPrice: number,
  countPerSide: number = 5
): { asks: OrderbookLevel[]; bids: OrderbookLevel[] } {
  const step = Math.max(midPrice * 0.001, 0.0001);
  const baseSize = 0.1;
  const asks: OrderbookLevel[] = [];
  const bids: OrderbookLevel[] = [];
  for (let i = 0; i < countPerSide; i++) {
    const askPrice = midPrice + (countPerSide - i) * step;
    const bidPrice = midPrice - (i + 1) * step;
    asks.push({
      price: askPrice,
      size: baseSize * (1 + i * 0.2),
      cumulative: 0,
      flash: null,
    });
    bids.push({
      price: bidPrice,
      size: baseSize * (1 + i * 0.2),
      cumulative: 0,
      flash: null,
    });
  }
  asks.sort((a, b) => b.price - a.price);
  bids.sort((a, b) => b.price - a.price);
  let cumA = 0,
    cumB = 0;
  const asksWithCum = asks.map((l) => {
    cumA += l.size;
    return { ...l, cumulative: cumA };
  });
  const bidsWithCum = bids.map((l) => {
    cumB += l.size;
    return { ...l, cumulative: cumB };
  });
  return { asks: asksWithCum, bids: bidsWithCum };
}

const DEFAULT_FAKE_PENDING: NonNullable<EnhancementConfig["fakePending"]> = {
  maxFakeLevelsPerSide: 3,
  minLifetimeMs: 2000,
  maxLifetimeMs: 8000,
  volumeScalePct: 0.8,
  minAddDelayMs: 1500,
  maxAddDelayMs: 5000,
};

export const DEFAULT_ENHANCEMENT_CONFIG: EnhancementConfig = {
  enabled: false,
  jitter: DEFAULT_JITTER,
  fakePending: DEFAULT_FAKE_PENDING,
};

/** Minimum price gap from mid to avoid crossing (scales with mid). */
const MIN_TICK_FROM_MID_PCT = 1e-6;

/**
 * Clamp value to ±amountPct around original (e.g. 0.001 => ±0.1%).
 * Uses a deterministic-ish offset from the value so the same level jitters consistently within a tick.
 */
function jitterValue(value: number, amountPct: number, seed: number): number {
  if (value === 0 || amountPct <= 0) return value;
  const range = value * amountPct;
  // Use seed to get a value in [-1, 1]; avoid Math.random() for testability / consistency per tick
  const t = (Math.sin(seed * 1000) + 1) / 2;
  const delta = (t - 0.5) * 2 * range;
  return Math.max(0, value + delta);
}

/**
 * Jitter price in a side-aware way so the book never crosses: asks stay >= mid + tick, bids <= mid - tick.
 * When side/midPrice are not provided, behaves like symmetric jitter (legacy).
 */
function jitterPriceWithBound(
  price: number,
  amountPct: number,
  seed: number,
  side: "ask" | "bid" | undefined,
  midPrice: number | undefined
): number {
  const jittered = jitterValue(price, amountPct, seed);
  if (side === undefined || midPrice === undefined || midPrice <= 0) return jittered;
  const tick = Math.max(midPrice * MIN_TICK_FROM_MID_PCT, 0.01);
  if (side === "ask") return Math.max(jittered, midPrice + tick);
  return Math.min(jittered, midPrice - tick);
}

export interface ApplyJitterOptions {
  /** When set, price jitter is clamped so this side never crosses mid. */
  side?: "ask" | "bid";
  /** Used with side to enforce best ask >= mid + tick, best bid <= mid - tick. */
  midPrice?: number;
}

/**
 * Apply micro-jitter to orderbook levels. Re-sorts by price (descending) and recomputes cumulative.
 * Both asks and bids in this codebase are displayed descending (highest first).
 * When options.side and options.midPrice are provided, price jitter is clamped so the book cannot cross
 * (asks stay >= mid, bids stay <= mid).
 */
export function applyJitter(
  levels: OrderbookLevel[],
  config: NonNullable<EnhancementConfig["jitter"]>,
  tickSeed: number,
  options?: ApplyJitterOptions
): EnhancedOrderbookLevel[] {
  if (levels.length === 0) return [];
  const pct = config.amountPct;
  const side = options?.side;
  const midPrice = options?.midPrice;
  const jittered = levels.map((level, i) => {
    const priceSeed = tickSeed + level.price * 1e6 + i;
    const sizeSeed = tickSeed + level.size * 1e3 + i * 7;
    return {
      ...level,
      price: jitterPriceWithBound(level.price, pct, priceSeed, side, midPrice),
      size: jitterValue(level.size, pct, sizeSeed),
      cumulative: 0, // recompute below
    };
  });
  // Sort descending (highest price first)
  jittered.sort((a, b) => b.price - a.price);
  let cumulative = 0;
  return jittered.map((level) => {
    cumulative += level.size;
    return { ...level, cumulative };
  });
}

export interface FakeLevel extends EnhancedOrderbookLevel {
  isPending: true;
  id: string;
  createdAt: number;
  expiresAt: number;
}

/** Spread range for fake levels: min/max distance from mid (as fraction of mid). */
const FAKE_SPREAD_PCT_MIN = 0.0005;
const FAKE_SPREAD_PCT_MAX = 0.0025;

/**
 * Create one fake level for a side. Price is offset from mid; size scaled from refSize.
 * Levels closer to mid are biased 50% larger in size so they appear more on the book.
 */
export function createFakeLevel(
  side: "ask" | "bid",
  midPrice: number,
  refSize: number,
  config: NonNullable<EnhancementConfig["fakePending"]>,
  id: string
): FakeLevel {
  const now = Date.now();
  const lifetime =
    config.minLifetimeMs +
    Math.random() * (config.maxLifetimeMs - config.minLifetimeMs);
  // Bias toward mid: use power so more fakes land close to mid (0.05%–0.25% from mid)
  const spreadPct =
    FAKE_SPREAD_PCT_MIN +
    Math.pow(Math.random(), 1.6) * (FAKE_SPREAD_PCT_MAX - FAKE_SPREAD_PCT_MIN);
  const priceOffset = midPrice * spreadPct * (side === "ask" ? 1 : -1);
  const price = midPrice + priceOffset;
  const baseSize = refSize * config.volumeScalePct * (0.7 + Math.random() * 0.6);
  // Pending orders approaching mid get ~50% more size on the book
  const proximityToMid =
    (FAKE_SPREAD_PCT_MAX - spreadPct) / (FAKE_SPREAD_PCT_MAX - FAKE_SPREAD_PCT_MIN);
  const nearMidSizeMultiplier = 1 + 0.5 * proximityToMid;
  const size = Math.max(0.01, baseSize * nearMidSizeMultiplier);
  const cumulative = size; // standalone level
  return {
    price,
    size,
    cumulative,
    flash: null,
    isPending: true,
    id,
    createdAt: now,
    expiresAt: now + lifetime,
  };
}

/**
 * Merge real (or jittered) levels with fake pending levels, sort by price descending.
 * Real levels are not marked isPending; fake levels are.
 */
export function mergeFakeLevels(
  realLevels: EnhancedOrderbookLevel[],
  fakeLevels: FakeLevel[],
  maxDepth: number
): EnhancedOrderbookLevel[] {
  const combined = [...realLevels, ...fakeLevels];
  combined.sort((a, b) => b.price - a.price);
  const limited = combined.slice(0, maxDepth);
  // Recompute cumulative for display (treat all as one book)
  let cumulative = 0;
  return limited.map((level) => {
    cumulative += level.size;
    return { ...level, cumulative };
  });
}
